import RefLineBase from './ref-line-base'
import * as turf from '@turf/turf'
import Decimal from 'decimal.js'

/**
 * 曲線基準線
 */
export default class CurveRefLine extends RefLineBase {
  // アプローチラインの角度を単位ベクトル合算で計算するためのポイント最小数
  static get MINIMUM_POINT_COUNT_FOR_APPROACH_LINE_BEARING_CALCULATION() {
    return 5
  }

  constructor(refLineGeoJson) {
    super(refLineGeoJson)
    this.westPoints5 = undefined
    this.eastPoints5 = undefined
  }

  prepare(workWidth) {
    const refCoordinates = this.refLineGeoJson.features[0].geometry.coordinates
    this.originalLine = turf.lineString(refCoordinates)

    const lastIndex = refCoordinates.length - 1

    this.startPoint = turf.point(refCoordinates[0])
    const startPointNext = turf.point(refCoordinates[1])
    this.endPoint = turf.point(refCoordinates[lastIndex])
    const endPointBefore = turf.point(refCoordinates[lastIndex - 1])

    // east westを決定
    const longitude1 = turf.getCoord(this.startPoint)[0]
    const longitude2 = turf.getCoord(this.endPoint)[0]

    // アプローチライン角度を単位ベクトル合算で計算するかどうか？
    const maxPointCount = CurveRefLine.MINIMUM_POINT_COUNT_FOR_APPROACH_LINE_BEARING_CALCULATION
    const unitVectorCalcForApproachLine = refCoordinates.length >= maxPointCount

    // 大きいほうが東側
    if (longitude1 > longitude2) {
      this.points.east = this.startPoint
      this.points.eastNext = startPointNext
      this.points.west = this.endPoint
      this.points.westNext = endPointBefore
      if (unitVectorCalcForApproachLine) {
        this.eastPoints5 = []
        for (let i = 0; i < maxPointCount; i++) {
          this.eastPoints5.push(turf.point(refCoordinates[i]))
        }
        this.westPoints5 = []
        // eslint-disable-next-line for-direction
        for (let i = lastIndex; i > lastIndex - maxPointCount; i--) {
          this.westPoints5.push(turf.point(refCoordinates[i]))
        }
      }
    } else {
      this.points.east = this.endPoint
      this.points.eastNext = endPointBefore
      this.points.west = this.startPoint
      this.points.westNext = startPointNext
      if (unitVectorCalcForApproachLine) {
        this.eastPoints5 = []
        // eslint-disable-next-line for-direction
        for (let i = lastIndex; i > lastIndex - maxPointCount; i--) {
          this.eastPoints5.push(turf.point(refCoordinates[i]))
        }
        this.westPoints5 = []
        for (let i = 0; i < maxPointCount; i++) {
          this.westPoints5.push(turf.point(refCoordinates[i]))
        }
      }
    }

    // 東西を結ぶ距離
    const length = turf.distance(this.points.west, this.points.east, RefLineBase.UNIT)

    // bearingは西から東
    const bearing = turf.bearing(this.points.west, this.points.east)

    const blockLength = 1000 // 1000m四方
    const edgeLength = blockLength / 2 - length / 2

    let coord = null
    let nextCoord = null

    // 西側オフセット
    let additionDistance = workWidth
    this.westOffsetLines = []
    while (additionDistance < blockLength / 2) {
      let offsetCoordinates = []
      for (let i = 0; i < refCoordinates.length; i++) {
        coord = refCoordinates[i]
        nextCoord = turf.destination(coord, additionDistance, bearing + 90, RefLineBase.UNIT)
        offsetCoordinates.push(turf.getCoord(nextCoord))
      }
      const offsetLine = turf.lineString(offsetCoordinates)
      this.westOffsetLines.push(offsetLine)
      additionDistance += workWidth
    }

    // 西側アプローチラインの計算
    let westApproachBearing = 0
    if (unitVectorCalcForApproachLine) {
      westApproachBearing = this.calculateBearingAverage(this.westPoints5)
    } else {
      westApproachBearing = turf.bearing(this.points.west, this.points.westNext)
    }
    let approachDistancePoint = turf.destination(
      this.points.west,
      edgeLength,
      westApproachBearing - 180,
      RefLineBase.UNIT
    )
    this.approachLineWest = turf.lineString([turf.getCoord(this.points.west), turf.getCoord(approachDistancePoint)])

    // 西側アプローチラインオフセット（Plus）
    this.westOffsetApproachLinesPlus = []
    additionDistance = workWidth
    coord = turf.getCoords(this.approachLineWest)[0]
    nextCoord = turf.getCoords(this.approachLineWest)[1]
    while (additionDistance < blockLength / 2) {
      const offsetPoint = turf.destination(coord, additionDistance, bearing + 90, RefLineBase.UNIT)
      const offsetNextPoint = turf.destination(nextCoord, additionDistance, bearing + 90, RefLineBase.UNIT)
      this.westOffsetApproachLinesPlus.push(
        turf.lineString([turf.getCoord(offsetPoint), turf.getCoord(offsetNextPoint)])
      )
      additionDistance += workWidth
    }

    // 西側アプローチラインオフセット（Minus）
    this.westOffsetApproachLinesMinus = []
    additionDistance = workWidth
    while (additionDistance < blockLength / 2) {
      const offsetPoint = turf.destination(coord, additionDistance, bearing - 90, RefLineBase.UNIT)
      const offsetNextPoint = turf.destination(nextCoord, additionDistance, bearing - 90, RefLineBase.UNIT)
      this.westOffsetApproachLinesMinus.push(
        turf.lineString([turf.getCoord(offsetPoint), turf.getCoord(offsetNextPoint)])
      )
      additionDistance += workWidth
    }

    // 東側オフセット
    additionDistance = workWidth
    this.eastOffsetLines = []
    while (additionDistance < blockLength / 2) {
      let offsetCoordinates = []
      for (let i = 0; i < refCoordinates.length; i++) {
        const coords = refCoordinates[i]
        const nextCoords = turf.destination(coords, additionDistance, bearing - 90, RefLineBase.UNIT)
        offsetCoordinates.push(nextCoords.geometry.coordinates)
      }
      const offsetLine = turf.lineString(offsetCoordinates)
      this.eastOffsetLines.push(offsetLine)
      additionDistance += workWidth
    }

    // 東側アプローチラインの計算
    let eastApproachBearing = 0
    if (unitVectorCalcForApproachLine) {
      eastApproachBearing = this.calculateBearingAverage(this.eastPoints5) + 180
    } else {
      eastApproachBearing = turf.bearing(this.points.eastNext, this.points.east)
    }
    approachDistancePoint = turf.destination(this.points.east, edgeLength, eastApproachBearing, RefLineBase.UNIT)
    this.approachLineEast = turf.lineString([turf.getCoord(this.points.east), turf.getCoord(approachDistancePoint)])

    // 東側アプローチラインオフセット（Plus）
    this.eastOffsetApproachLinesPlus = []
    additionDistance = workWidth
    coord = turf.getCoords(this.approachLineEast)[0]
    nextCoord = turf.getCoords(this.approachLineEast)[1]
    while (additionDistance < blockLength / 2) {
      let offsetPoint = turf.destination(coord, additionDistance, bearing + 90, RefLineBase.UNIT)
      let offsetNextPoint = turf.destination(nextCoord, additionDistance, bearing + 90, RefLineBase.UNIT)
      this.eastOffsetApproachLinesPlus.push(
        turf.lineString([turf.getCoord(offsetPoint), turf.getCoord(offsetNextPoint)])
      )
      additionDistance += workWidth
    }

    // 東側アプローチラインオフセット（Minus）
    additionDistance = workWidth
    this.eastOffsetApproachLinesMinus = []
    while (additionDistance < blockLength / 2) {
      const offsetPoint = turf.destination(coord, additionDistance, bearing - 90, RefLineBase.UNIT)
      const offsetNextPoint = turf.destination(nextCoord, additionDistance, bearing - 90, RefLineBase.UNIT)
      this.eastOffsetApproachLinesMinus.push(
        turf.lineString([turf.getCoord(offsetPoint), turf.getCoord(offsetNextPoint)])
      )
      additionDistance += workWidth
    }

    // 中心
    this.center = turf.centroid(this.refLineGeoJson).geometry.coordinates
    this.boundingBox = turf.bbox(this.refLineGeoJson)
  }

  get redMultiLineGeoJson() {
    let lines = []
    lines.push(this.originalLine)
    lines.push(this.approachLineWest)
    lines.push(this.approachLineEast)
    this._appendPointsOrLine(lines, this.westOffsetLines, { filter: 'odd', type: 'line' })
    this._appendPointsOrLine(lines, this.eastOffsetLines, { filter: 'odd', type: 'line' })
    this._appendPointsOrLine(lines, this.westOffsetApproachLinesPlus, { filter: 'odd', type: 'line' })
    this._appendPointsOrLine(lines, this.westOffsetApproachLinesMinus, { filter: 'odd', type: 'line' })
    this._appendPointsOrLine(lines, this.eastOffsetApproachLinesPlus, { filter: 'odd', type: 'line' })
    this._appendPointsOrLine(lines, this.eastOffsetApproachLinesMinus, { filter: 'odd', type: 'line' })
    return this.toMultiLineString(lines)
  }

  get blueMultiLineGeoJson() {
    let lines = []
    this._appendPointsOrLine(lines, this.westOffsetLines, { filter: 'even', type: 'line' })
    this._appendPointsOrLine(lines, this.eastOffsetLines, { filter: 'even', type: 'line' })
    this._appendPointsOrLine(lines, this.westOffsetApproachLinesPlus, { filter: 'even', type: 'line' })
    this._appendPointsOrLine(lines, this.westOffsetApproachLinesMinus, { filter: 'even', type: 'line' })
    this._appendPointsOrLine(lines, this.eastOffsetApproachLinesPlus, { filter: 'even', type: 'line' })
    this._appendPointsOrLine(lines, this.eastOffsetApproachLinesMinus, { filter: 'even', type: 'line' })
    return this.toMultiLineString(lines)
  }

  calculateBearingAverage(points) {
    let angles = []
    for (let i = 0; i < points.length - 1; i++) {
      const angle = turf.bearing(turf.getCoord(points[i]), turf.getCoord(points[i + 1]))
      angles.push(angle)
    }

    let xSum = 0
    let ySum = 0

    for (let i = 0; i < angles.length; i++) {
      const angleRad = turf.degrees2radians(angles[i])
      const x = Math.cos(angleRad)
      const y = Math.sin(angleRad)
      xSum += x
      ySum += y
    }

    const xAvg = new Decimal(xSum).dividedBy(angles.length).toNumber()
    const yAvg = new Decimal(ySum).dividedBy(angles.length).toNumber()

    const avgRad = Decimal.atan2(yAvg, xAvg)
    return turf.radians2degrees(avgRad)
  }
}
